<template>
    <figure :class="`image is-${size} is-avatar`" :title="title">
        <div v-if="hasValidImageUrl && !imageLoadError" class="avatar-image">
            <img :src="imgUrl" alt="" class="avatar-img" @error="handleImageError" />
        </div>
        <div v-else class="initials-avatar" :style="initialsAvatarStyle">
            {{ calculatedInitials }}
        </div>
    </figure>
</template>

<script>
import { initials } from '@/Helpers.js';

export default {
    name: 'UserAvatar',

    props: {
        size: {
            type: String,
            default: '48x48',
        },

        imgUrl: {
            type: null,
            default: null,
        },

        title: {
            type: null,
            default: null,
        },

        user: {
            type: Object,
            default: null,
        },

        backgroundColor: {
            type: String,
            default: null, // Will use a dynamically generated color if null
        },
    },

    data() {
        return {
            imageLoadError: false,
        };
    },

    computed: {
        hasValidImageUrl() {
            // More explicit check for valid image URL
            return (
                this.imgUrl !== null &&
                this.imgUrl !== undefined &&
                typeof this.imgUrl === 'string' &&
                this.imgUrl.trim() !== ''
            );
        },

        calculatedInitials() {
            // First try to get initials from user object
            if (this.user) {
                // Handle both User models (which have an initials property) and plain objects
                if (this.user.initials) {
                    return this.user.initials;
                }

                // Fall back to calculating from first_name and last_name
                return initials(this.user.first_name, this.user.last_name);
            }

            // If no user object, try to extract from title/name
            if (this.title) {
                const name = typeof this.title === 'string' ? this.title.trim() : '';
                const nameParts = name.split(' ');

                if (nameParts.length >= 2) {
                    return initials(nameParts[0], nameParts[nameParts.length - 1]);
                }
                if (nameParts.length === 1) {
                    return initials(nameParts[0], '');
                }
            }

            return '';
        },

        defaultAvatarUrl() {
            // Fallback to Gravatar or any default image
            if (this.title) {
                const email = typeof this.title === 'string' ? this.title.trim().toLowerCase() : '';
                const hash = this.hashEmail(email);
                return `https://www.gravatar.com/avatar/${hash}.jpg?s=200&d=mm`;
            }

            return '';
        },

        initialsAvatarStyle() {
            const nameForColor = this.user?.name || this.title || this.calculatedInitials;

            return {
                backgroundColor: this.backgroundColor || this.generateColorFromString(nameForColor),
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: this.calculateFontSize(),
                fontWeight: 'bold',
            };
        },
    },

    watch: {
        imgUrl() {
            // Reset error state when imgUrl changes
            this.imageLoadError = false;
        },
    },

    methods: {
        handleImageError() {
            this.imageLoadError = true;
        },

        hashEmail(email) {
            // Simple MD5-like hash function for demo purposes
            // In a real app, you would use a proper MD5 library or handle this server-side
            let hash = 0;
            if (email.length === 0) return hash.toString();

            for (let i = 0; i < email.length; i++) {
                const char = email.charCodeAt(i);
                // eslint-disable-next-line no-bitwise
                hash = (hash << 5) - hash + char;
                // eslint-disable-next-line no-bitwise
                hash &= hash; // Convert to 32bit integer
            }

            return Math.abs(hash).toString(16);
        },

        generateColorFromString(str) {
            if (!str) return '#6c757d'; // Default gray

            // Simple string hash to generate a color
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                // eslint-disable-next-line no-bitwise
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }

            // Generate a hue between 0 and 360
            const h = Math.abs(hash) % 360;

            // Use a fixed saturation and lightness for readability
            return `hsl(${h}, 65%, 55%)`;
        },

        calculateFontSize() {
            const [width] = this.size.split('x');
            const numWidth = parseInt(width, 10);
            return `${Math.max(numWidth / 2.5, 10)}px`;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.is-avatar {
    border-radius: 50%;
    overflow: hidden;

    div {
        height: 100%;
        width: 100%;
    }

    .avatar-image {
        height: 100%;
        width: 100%;
    }

    .avatar-img {
        height: 100%;
        width: 100%;
        object-fit: cover;
    }

    .initials-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        text-align: center;
        color: white;
    }
}
</style>
